import '../scrollytelling.css'
import '../v3/office.css'
import '../v7/baukasten.css'
import '../v9/woche.css'
import './werdegang.css'

import { ArrowDownIcon, CheckIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { AIVY_MEETING_URL } from '../../../helpers'
import { SceneProvider, useFrame, useScrollStory } from '../engine'
import { anim, lerp, seg, smooth } from '../motion'
import { BeatsProvider } from '../v3/beats'
import {
  AtsPanel,
  FairPanel,
  GamePanel,
  InterviewCard,
  MagicPanel,
  OptionCards,
  PdfPanel,
  ProfilePanel,
  RulePanel,
  SupportPanel,
  TalentPhone
} from '../v3/panels'
import { CrossMock, LinkMock, TalentList, TeamMock } from '../v9/mocks'
import { BEATS, CAMERA, CHAPTERS, TOTAL_VH, atB, inB } from './beats'
import { APPS, STEPS } from './copy'
import { AnalysisStack, GrowthMock } from './mocks'
import { WORLD, World } from './world'

/**
 * Version 10 „Lenas Werdegang“, für HR-Teams und Recruiter:innen. Das
 * Unternehmen als Spielbrett: Eine Lücke im Team wird gemeldet, gemeinsam
 * definiert, gesucht und mit Aivy gefüllt. Danach wächst Lena, und die
 * nächste Lücke startet nicht bei null. Oben links genau ein Gedanke, rechts
 * das passende Stück Produkt, unten der Lebenslauf als Zeitleiste.
 */

const BEATS_VALUE = { inB, atB }
const BY_ID = Object.fromEntries(BEATS.map((b) => [b.id, b]))
const MOBILE = 720

export default function ScrollytellingV10() {
  const story = useScrollStory(1, { mode: 'pin' })

  const jump = (l) => {
    const el = story.chapterRefs.current[0]
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: top + l * (el.offsetHeight - window.innerHeight),
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  return (
    <div className='sty-root v10-root'>
      <div ref={story.storyRef}>
        <section
          ref={(el) => (story.chapterRefs.current[0] = el)}
          className='v10-section'
          style={{ height: `${TOTAL_VH}vh` }}
          aria-label='Lenas Werdegang, von der Lücke im Team bis zur nächsten Stelle'
        >
          <div
            ref={(el) => (story.sceneRefs.current[0] = el)}
            className='v10-sticky'
          >
            <SceneProvider index={0} subscribe={story.subscribe}>
              <BeatsProvider value={BEATS_VALUE}>
                <Stage onJump={jump} />
              </BeatsProvider>
            </SceneProvider>
          </div>
        </section>
      </div>
    </div>
  )
}

// ---------- Kamera ----------

function cameraAt(l) {
  const K = CAMERA
  if (l <= K[0].l) return K[0]
  for (let i = 1; i < K.length; i++) {
    if (l > K[i].l) continue
    const a = K[i - 1]
    const c = K[i]
    const p = c.l > a.l ? smooth(seg(l, a.l, c.l)) : 1
    // Zoom geometrisch, sonst wirkt das Rein- und Rauszoomen ungleich schnell
    const zoom = (k) => a[k] * Math.pow(c[k] / a[k], p)
    return {
      x: lerp(a.x, c.x, p),
      y: lerp(a.y, c.y, p),
      z: zoom('z'),
      fx: lerp(a.fx, c.fx, p),
      mx: lerp(a.mx, c.mx, p),
      my: lerp(a.my, c.my, p),
      mz: zoom('mz'),
      mfy: lerp(a.mfy, c.mfy, p)
    }
  }
  return K[K.length - 1]
}

function Camera() {
  const ref = useRef(null)
  useFrame((l) => {
    const el = ref.current
    if (!el) return
    const box = el.parentElement
    const vw = box.clientWidth
    const vh = box.clientHeight
    const mobile = vw < MOBILE
    const c = cameraAt(l)
    const s = mobile ? (vw / 760) * c.mz : Math.min(vw / 1440, vh / 860) * c.z
    const x = mobile ? c.mx : c.x
    const y = mobile ? c.my : c.y
    const fx = mobile ? 0.5 : c.fx
    const fy = mobile ? c.mfy : 0.56
    el.style.transform = `translate3d(${(fx * vw - x * s).toFixed(1)}px, ${(fy * vh - y * s).toFixed(1)}px, 0) scale(${s.toFixed(4)})`
  })
  return (
    <div
      ref={ref}
      className='v10-world'
      style={{ width: WORLD.w, height: WORLD.h }}
    >
      <World />
    </div>
  )
}

// ---------- Bühne ----------

function Stage({ onJump }) {
  const [active, setActive] = useState(0)
  useFrame((l) => {
    const i = BEATS.findIndex((b) => l >= b.at[0] && l < b.at[1])
    setActive(i === -1 ? BEATS.length - 1 : i)
  })
  const beat = BEATS[active]

  return (
    <div className='v10-stage'>
      <Camera />
      <div className='v10-scrim' aria-hidden='true' />
      <Intro />
      <Copy />
      <Panels />
      <Timeline chapter={beat.ch} onJump={onJump} />
    </div>
  )
}

Stage.propTypes = { onJump: PropTypes.func.isRequired }

function Intro() {
  const [s, e] = BY_ID.intro.at
  const len = e - s
  return (
    <div
      className='v10-intro'
      style={anim({ at: [-1, 0], out: [s + len * 0.55, e], oy: -40 })}
    >
      <div className='off-eyebrow'>Für HR-Teams und Recruiter:innen</div>
      <h1 className='v10-intro__title'>{STEPS.intro.title}</h1>
      <p className='v10-intro__line'>{STEPS.intro.line}</p>
      <span className='v10-intro__hint'>
        Scroll los <ArrowDownIcon width={16} height={16} />
      </span>
    </div>
  )
}

const chapterOf = (id) => CHAPTERS.find((c) => c.id === BY_ID[id].ch)

/** Oben links, immer genau eine Station sichtbar. */
function Copy() {
  return (
    <div className='v10-copy'>
      {Object.keys(STEPS)
        .filter((id) => id !== 'intro')
        .map((id) => {
          const [s, e] = BY_ID[id].at
          const len = e - s
          const last = id === 'finale'
          const ch = chapterOf(id)
          return (
            <div
              key={id}
              className={clsx('v10-copy__step', last && 'is-final')}
              style={anim({
                at: [s + len * 0.03, s + len * 0.14],
                out: last ? undefined : [e - len * 0.1, e],
                y: 24,
                oy: -24
              })}
            >
              {ch && (
                <div className='v10-copy__date'>
                  {ch.date} · {ch.title}
                </div>
              )}
              <h2 className='v10-copy__title'>{STEPS[id].title}</h2>
              <p className='v10-copy__line'>{STEPS[id].line}</p>
              {last && <Ctas />}
            </div>
          )
        })}
    </div>
  )
}

function Ctas() {
  return (
    <div className='v10-ctas' style={anim({ at: inB('finale', 0.4, 0.5) })}>
      <Link to='/career/new' className='off-cta'>
        Stelle anlegen
      </Link>
      <a
        href={AIVY_MEETING_URL}
        target='_blank'
        rel='noreferrer'
        className='off-cta off-cta--ghost'
      >
        Gespräch vereinbaren
      </a>
    </div>
  )
}

// ---------- Panels rechts ----------

function Slot({ id, className, children }) {
  const [s, e] = BY_ID[id].at
  const len = e - s
  return (
    <div
      className={clsx('v10-slot', className)}
      style={anim({
        at: [s + len * 0.02, s + len * 0.1],
        out: [e - len * 0.08, e],
        y: 30,
        s: 0.96
      })}
    >
      {children}
    </div>
  )
}

Slot.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node
}

function Panels() {
  return (
    <div className='v10-panels'>
      <Slot id='hr'>
        <OptionCards />
      </Slot>
      <Slot id='analyse'>
        <AnalysisStack />
      </Slot>
      <ProfilePanel />
      <LinkMock />
      <GamePanel />
      <Slot id='talente'>
        <TalentPhone />
      </Slot>
      <Slot id='magic'>
        <MagicPanel />
      </Slot>
      <TalentList apps={APPS} />
      <RulePanel />
      <PdfPanel />
      <Slot id='gespraech' className='v10-slot--interview'>
        <InterviewCard />
      </Slot>
      <TeamMock />
      <CrossMock />
      <FairPanel />
      <AtsPanel />
      <GrowthMock />
      <SupportPanel />
    </div>
  )
}

// ---------- Lebenslauf als Zeitleiste ----------

const START = Object.fromEntries(
  CHAPTERS.map((c) => [c.id, BEATS.find((b) => b.ch === c.id).at[0]])
)

function Timeline({ chapter, onJump }) {
  const index = CHAPTERS.findIndex((c) => c.id === chapter)
  const done = chapter === 'end' ? CHAPTERS.length : index
  const [s] = BY_ID.luecke.at
  return (
    <nav
      className='v10-cv'
      aria-label='Lenas Werdegang'
      style={anim({ at: [s * 0.6, s], y: 30 })}
    >
      <span className='v10-cv__head'>Lenas Werdegang</span>
      <ol className='v10-cv__list'>
        {CHAPTERS.map((c, i) => (
          <li
            key={c.id}
            className={clsx(
              'v10-cv__item',
              i === index && 'is-active',
              i < done && 'is-done'
            )}
          >
            <button
              type='button'
              onClick={() => onJump(START[c.id] + 0.001)}
              aria-current={i === index ? 'step' : undefined}
            >
              <span className='v10-cv__dot'>
                {i < done && <CheckIcon width={10} height={10} />}
              </span>
              <span className='v10-cv__date'>{c.date}</span>
              <span className='v10-cv__title'>{c.title}</span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  )
}

Timeline.propTypes = {
  chapter: PropTypes.string.isRequired,
  onJump: PropTypes.func.isRequired
}
